import axios from "axios";
import { Users, FileText } from "lucide-react";
import { PiMicrosoftExcelLogoLight } from "react-icons/pi";
import { useEffect, useState } from "react";
import ReportFilters from "./ReportFilters";
import { buildPeriodRange } from "../../../utils/helpers";

/* -------------------- KPI CARD -------------------- */
const KPI = ({ title, value, subtext }) => (
  <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4">
    <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
      {title}
    </p>
    <p className="text-2xl font-semibold text-gray-900">{value}</p>
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

const SkeletonKPI = () => (
  <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4 animate-pulse">
    <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-8 w-32 bg-gray-200 rounded" />
  </div>
);

const SkeletonTable = () => (
  <div className="space-y-2 animate-pulse">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="h-12 bg-gray-100 rounded" />
    ))}
  </div>
);

/* -------------------- DEFAULT FILTERS -------------------- */
const DEFAULT_FILTERS = {
  fromDate: "",
  toDate: "",
  reportType: "monthly",
};

/* -------------------- MAIN COMPONENT -------------------- */
const StaffReports = ({ branchId }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const [kpis, setKpis] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(false);

  /* 🔁 Sync dates with report type */
  useEffect(() => {
    if (!filters.reportType) return;
    const { fromDate, toDate } = buildPeriodRange(filters.reportType);
    setFilters((p) => ({ ...p, fromDate, toDate }));
  }, [filters.reportType]);

  /* 📡 Fetch Staff Report */
  const fetchStaffReport = async () => {
    try {
      setLoading(true);

      const params = {
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      };

      if (branchId) params.branchId = branchId;

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/reports/staff`,
        { params },
      );

      setKpis(res.data.kpis || []);
      setPerformance(res.data.performance || []);
    } catch (err) {
      console.error("Failed to fetch staff report", err);
      setKpis([]);
      setPerformance([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaffReport();
  }, [appliedFilters, branchId]);

  return (
    <div className="space-y-6">
      {/* FILTER BAR */}
      <ReportFilters
        filters={filters}
        appliedFilters={appliedFilters}
        setFilters={setFilters}
        showStaff={false}
        onApply={() => setAppliedFilters(filters)}
        onClearAll={() => {
          setFilters(DEFAULT_FILTERS);
          setAppliedFilters(DEFAULT_FILTERS);
        }}
        onRemoveFilter={(key) => {
          setFilters((p) => ({ ...p, [key]: "" }));
          setAppliedFilters((p) => ({ ...p, [key]: "" }));
        }}
        /* 🔓 EXPORT ALWAYS ENABLED FOR STAFF */
        onExportPDF={() =>
          alert(`Exporting ${appliedFilters.reportType} Staff Report as PDF`)
        }
        onExportExcel={() =>
          alert(`Exporting ${appliedFilters.reportType} Staff Report as Excel`)
        }
      />

      {/* KPI GRID */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <SkeletonKPI key={i} />)
          : kpis
              .slice()
              .sort((a, b) => a.seq - b.seq)
              .map((kpi, i) => <KPI key={i} {...kpi} />)}
      </div>

      {/* CONTENT */}
      <div className="bg-white border border-[#F7BFAD] rounded-2xl p-6">
        {loading ? (
          <SkeletonTable />
        ) : !performance.length ? (
          <div className="h-40 flex items-center justify-center text-sm text-gray-400">
            No staff data available
          </div>
        ) : (
          <>
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Users className="size-5 text-[#f0653a]" />
              Staff Performance Leaderboard
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border rounded-md overflow-hidden">
                <thead className="bg-[#F7BFAD]/40">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">
                      Staff Name
                    </th>
                    <th className="px-4 py-3 text-center font-semibold">
                      Jobs
                    </th>
                    <th className="px-4 py-3 text-center font-semibold">
                      Revenue
                    </th>
                    <th className="px-4 py-3 text-center font-semibold">
                      Completion Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {performance.map((s, i) => (
                    <tr key={i} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{s.name}</td>
                      <td className="text-center">{s.jobs}</td>
                      <td className="text-center">
                        ₹{s.revenue.toLocaleString()}
                      </td>
                      <td className="text-center">
                        <span className="text-green-600 font-semibold">
                          {s.completionRate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StaffReports;

// import axios from "axios";
// import { Users } from "lucide-react";
// import { useEffect, useState } from "react";
// import ReportFilters from "./ReportFilters";
// import { buildPeriodRange } from "../../../utils/helpers";

// /* -------------------- KPI CARD -------------------- */
// const KPI = ({ title, value, subtext }) => (
//   <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4">
//     <p className="text-xs uppercase tracking-wide text-gray-400 mb-2">
//       {title}
//     </p>
//     <p className="text-2xl font-semibold text-gray-900">{value}</p>
//     {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
//   </div>
// );

// const SkeletonKPI = () => (
//   <div className="bg-white border border-[#F7BFAD] rounded-2xl p-4 animate-pulse">
//     <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
//     <div className="h-8 w-32 bg-gray-200 rounded" />
//   </div>
// );

// const SkeletonTable = () => (
//   <div className="space-y-2 animate-pulse">
//     {[1, 2, 3, 4, 5].map((i) => (
//       <div key={i} className="h-12 bg-gray-100 rounded" />
//     ))}
//   </div>
// );

// /* -------------------- DEFAULT FILTERS -------------------- */
// const DEFAULT_FILTERS = {
//   fromDate: "",
//   toDate: "",
//   reportType: "monthly",
//   staff: "",
// };

// /* -------------------- MAIN -------------------- */
// const StaffReports = ({ branchId }) => {
//   const [filters, setFilters] = useState(DEFAULT_FILTERS);
//   const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

//   const [staffList, setStaffList] = useState([]);
//   const [kpis, setKpis] = useState([]);
//   const [performance, setPerformance] = useState([]);

//   const [loading, setLoading] = useState(false);

//   /* 🔁 Sync dates with report type */
//   useEffect(() => {
//     if (!filters.reportType) return;
//     const { fromDate, toDate } = buildPeriodRange(filters.reportType);
//     setFilters((p) => ({ ...p, fromDate, toDate }));
//   }, [filters.reportType]);

//   /* 📡 Fetch Staff Report */
//   const fetchStaffReport = async () => {
//     try {
//       setLoading(true);

//       const params = {
//         fromDate: appliedFilters.fromDate,
//         toDate: appliedFilters.toDate,
//       };

//       if (branchId) params.branchId = branchId;
//       if (appliedFilters.staff) params.staffId = appliedFilters.staff;

//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/reports/staff`,
//         { params },
//       );

//       const api = res.data;

//       setKpis(api.kpis || []);
//       setPerformance(api.performance || []);

//       /* Populate staff dropdown */
//       if (api.performance?.length) {
//         setStaffList(
//           api.performance.map((s) => ({
//             id: s.staffId,
//             name: s.name,
//           })),
//         );
//       }
//     } catch (err) {
//       console.error("Failed to fetch staff report", err);
//       setKpis([]);
//       setPerformance([]);
//       setStaffList([]);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchStaffReport();
//   }, [appliedFilters, branchId]);

//   return (
//     <div className="space-y-6">
//       {/* FILTER BAR */}
//       <ReportFilters
//         filters={filters}
//         appliedFilters={appliedFilters}
//         setFilters={setFilters}
//         staffList={staffList}
//         showStaff
//         onApply={() => setAppliedFilters(filters)}
//         onClearAll={() => {
//           setFilters(DEFAULT_FILTERS);
//           setAppliedFilters(DEFAULT_FILTERS);
//         }}
//         onRemoveFilter={(key) => {
//           setFilters((p) => ({ ...p, [key]: "" }));
//           setAppliedFilters((p) => ({ ...p, [key]: "" }));
//         }}
//         onExportPDF={() =>
//           alert(`Exporting ${appliedFilters.reportType} Staff PDF`)
//         }
//         onExportExcel={() =>
//           alert(`Exporting ${appliedFilters.reportType} Staff Excel`)
//         }
//       />

//       {/* KPI GRID */}
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//         {loading
//           ? Array.from({ length: 4 }).map((_, i) => <SkeletonKPI key={i} />)
//           : kpis
//               .sort((a, b) => a.seq - b.seq)
//               .map((kpi, i) => <KPI key={i} {...kpi} />)}
//       </div>

//       {/* CONTENT */}
//       <div className="bg-white border border-[#F7BFAD] rounded-2xl p-6">
//         {loading ? (
//           <SkeletonTable />
//         ) : !performance.length ? (
//           <div className="h-40 flex items-center justify-center text-sm text-gray-400">
//             No staff data available
//           </div>
//         ) : (
//           <>
//             <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
//               <Users className="size-5 text-[#f0653a]" />
//               Staff Performance Leaderboard
//             </h3>

//             <div className="overflow-x-auto">
//               <table className="w-full text-sm border rounded-md overflow-hidden">
//                 <thead className="bg-[#F7BFAD]/40">
//                   <tr>
//                     <th className="px-4 py-3 text-left font-semibold">
//                       Staff Name
//                     </th>
//                     <th className="px-4 py-3 text-center font-semibold">
//                       Jobs
//                     </th>
//                     <th className="px-4 py-3 text-center font-semibold">
//                       Revenue
//                     </th>
//                     <th className="px-4 py-3 text-center font-semibold">
//                       Completion Rate
//                     </th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {performance.map((s, i) => (
//                     <tr key={i} className="border-t hover:bg-gray-50">
//                       <td className="px-4 py-3 font-medium">{s.name}</td>
//                       <td className="text-center">{s.jobs}</td>
//                       <td className="text-center">
//                         ₹{s.revenue.toLocaleString()}
//                       </td>
//                       <td className="text-center">
//                         <span className="text-green-600 font-semibold">
//                           {s.completionRate}%
//                         </span>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </>
//         )}
//       </div>
//     </div>
//   );
// };

// export default StaffReports;
